import React, { useContext, useEffect, useRef, useState } from "react";
import { Phone, ChevronUp, ChevronDown } from "lucide-react";
import ReactStars from "react-rating-stars-component";
import AiRetryNotice from "./AiRetryNotice";
import { Context } from "Context/Context";
import { clickedFirmApi, getSavedFirmsApi, saveFirmApi, unsaveFirmApi } from "Constants";
import { toast } from "react-toastify";

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();

function AiFirms({
  localFirms = [],
  smallFirms = [],
  caseTitle = "",
  caseId,
  firmMatchStatus,
  setUpdateComp,
}) {
  const {makeApiCallFunc} = useContext(Context);
  const [updateSave, setUpdateSave] = useState(false);
  const [savedFirms, setSavedFirms] = useState([]);
  const [firmsToShow, setFirmsToShow] = useState([...localFirms, ...smallFirms]);
  const sortFirms = (savedFirmsList) => {
    setFirmsToShow(
            [...localFirms, ...smallFirms].sort((a, b) => {
              const aSaved = savedFirmsList?.filter(saved => saved.firmId === a.firmid).length > 0;
              const bSaved = savedFirmsList?.filter(saved => saved.firmId === b.firmid).length > 0;
              if(aSaved && !bSaved) return -1;
              if(!aSaved && bSaved) return 1;
              return 0;
            })
          )
        };
  // Keep the visible list in sync when new firm data arrives later (e.g. after
  // a firm-match retry refreshes caseDetails) - firmsToShow previously only
  // read localFirms/smallFirms once, at mount, via useState's initializer.
  useEffect(() => {
    sortFirms(savedFirms);
  }, [localFirms, smallFirms]);

  useEffect(() => {
    const getSavesForTheUser = () => {
       makeApiCallFunc(getSavedFirmsApi,
        {
          caseId
        },
        ({data}) => {
          setSavedFirms(data.savedFirms);
          //combine localFirms and smallFirms and rearrange the saved firms to top of the list.
          sortFirms(data.savedFirms);
          console.log(data.savedFirms)

        },
        (error) => console.log(error)
      )
    }
    getSavesForTheUser()
  }, [updateSave])
  const toggleSaveFirm = (firmId, isSaved) => {
    makeApiCallFunc(isSaved ? saveFirmApi : unsaveFirmApi,
      {
        firmId,
        caseId
      },
      (data) => {
        toast.success(isSaved ? "Firm saved" : "Firm unsaved. Save it anytime from the list again.", {
          position: "top-right",
          autoClose: 3000,});
        if(isSaved)
        {
          setSavedFirms([...savedFirms, { firmId }]);
          sortFirms([...savedFirms, { firmId }]);
        }
        else
        {
          const updatedSavedFirms = savedFirms.filter(saved => saved.firmId !== firmId);
          setSavedFirms(updatedSavedFirms);
          sortFirms(updatedSavedFirms);
        }
        console.log(data)},
      (data) => console.log(data),
      null,
      true
    )
  }
  const clickedFirm = (firmId) => {
    makeApiCallFunc(clickedFirmApi,
      {
        firmId,
        caseId
      },
      (data) => console.log(data),
      (data) => console.log(data),
      null,
      true
    )
  }

  const emailRef = useRef(null);

  // Design's firm list is a fixed-height scrollable panel with a small
  // scroll-progress track under the heading and an up/down control with its
  // own track beside the cards (section8a). These refs/state just mirror
  // native scroll position into those two indicators - no data/logic change.
  const listRef = useRef(null);
  const [scrollFrac, setScrollFrac] = useState(0);
  const [thumbFrac, setThumbFrac] = useState(1);

  const updateScrollMetrics = () => {
    const el = listRef.current;
    if (!el) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    setScrollFrac(maxScroll > 0 ? el.scrollTop / maxScroll : 0);
    setThumbFrac(el.scrollHeight > 0 ? Math.min(1, el.clientHeight / el.scrollHeight) : 1);
  };

  useEffect(() => {
    updateScrollMetrics();
  }, [firmsToShow]);

  const scrollList = (dir) => {
    listRef.current?.scrollBy({ top: dir * 148, behavior: "smooth" });
  };

  const FirmCard = ({ firm }) => {
    const isSaved = savedFirms?.filter(saved => saved.firmId === firm.firmid).length > 0;
    return (
    <div className="rounded-[10px] border border-[rgba(20,33,61,.12)] bg-ivory p-4 flex flex-col sm:flex-row sm:items-center gap-3">
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-navy text-white text-[13px] font-semibold flex-none">
          {getInitials(firm.name) || "?"}
        </div>

        <div className="min-w-0 flex-1">
          <h4 className="text-[14px] lg:text-[15px] font-semibold text-navy font-plexsans truncate">
            {firm.name}
          </h4>

          {firm.largest_settlement && (
            <p className="mt-1 pl-2.5 border-l-2 border-tan text-[12px] lg:text-[12.5px] italic text-[rgba(20,33,61,.6)] leading-snug">
              {firm.largest_settlement}
            </p>
          )}

          <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
            {firm.fitRate > 0 && (
              <span className="flex items-center gap-1.5">
                <ReactStars
                  count={5}
                  value={firm.fitRate}
                  size={13}
                  edit={false}
                  isHalf={true}
                  activeColor="#c9a26a"
                />
                <span className="text-[12px] text-[rgba(20,33,61,.5)]">{firm.fitRate}/5</span>
              </span>
            )}

            {firm.fitRate > 0 && firm.phone && (
              <span className="text-[rgba(20,33,61,.3)] text-[12px]">&middot;</span>
            )}

            {firm.phone && (
              <a
                href={`tel:${firm.phone}`}
                className="flex items-center gap-1.5 text-[12px] lg:text-[13px] text-[rgba(20,33,61,.55)]"
              >
                <Phone size={13} />
                {firm.phone}
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-none sm:ml-auto pt-3 sm:pt-0 border-t border-[rgba(20,33,61,.08)] sm:border-t-0">
        <button
          type="button"
          onClick={() => toggleSaveFirm(firm.firmid, !isSaved)}
          className={
            "flex-1 sm:flex-none flex items-center justify-center rounded-lg px-4 py-2 text-[13px] font-semibold whitespace-nowrap transition-colors " +
            (isSaved
              ? "bg-[rgba(20,33,61,.06)] text-navy"
              : "bg-tan text-white hover:bg-tanDark")
          }
        >
          {isSaved ? "Saved" : "Save Firm"}
        </button>
        {firm.website && (
          <button
            type="button"
            onClick={() => {
              window.open(firm.website, "_blank");
              clickedFirm(firm.firmid)
            }}
            className="flex-1 sm:flex-none flex items-center justify-center gap-1 rounded-lg border border-[rgba(20,33,61,.15)] px-4 py-2 text-[13px] font-semibold text-navy hover:border-tan transition-colors whitespace-nowrap"
          >
            Visit <span aria-hidden="true">→</span>
          </button>
        )}
      </div>
    </div>
  )};

  return (
    <div className="font-plexsans">
      <div ref={emailRef}>
        {firmsToShow?.length > 0 ? (
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-[16px] lg:text-[17px] font-semibold text-navy font-spectral">
                Recommended Law Firms
              </h3>
              <span className="text-[11px] font-medium text-tan bg-[rgba(138,98,49,.1)] rounded-full px-2.5 py-0.5 flex-none">
                {firmsToShow.length} matched
              </span>
            </div>

            {/* scroll-position track, mirrors the panel's own scroll below */}
            <div className="relative h-[3px] w-full bg-[rgba(20,33,61,.08)] rounded-full mb-4 overflow-hidden">
              <div
                className="absolute top-0 h-full bg-tan rounded-full"
                style={{
                  width: `${thumbFrac * 100}%`,
                  left: `${scrollFrac * (100 - thumbFrac * 100)}%`,
                }}
              />
            </div>

            <div className="flex items-stretch gap-2">
              <div
                ref={listRef}
                onScroll={updateScrollMetrics}
                className="flex-1 min-w-0 max-h-[400px] overflow-y-auto pr-0.5 space-y-2.5 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
              >
                {firmsToShow.map((firm, index) => (
                  <FirmCard
                    key={index}
                    firm={firm}
                  />
                ))}
              </div>

              {firmsToShow.length > 2 && thumbFrac < 1 && (
                <div className="hidden sm:flex flex-col items-center justify-between gap-1.5 py-1 flex-none">
                  <button
                    type="button"
                    onClick={() => scrollList(-1)}
                    aria-label="Scroll firms up"
                    className="w-5 h-5 flex items-center justify-center text-[rgba(20,33,61,.35)] hover:text-tan transition-colors"
                  >
                    <ChevronUp size={14} />
                  </button>
                  <div className="relative w-[3px] flex-1 bg-[rgba(20,33,61,.08)] rounded-full overflow-hidden">
                    <div
                      className="absolute left-0 w-full bg-[rgba(20,33,61,.3)] rounded-full"
                      style={{
                        height: `${thumbFrac * 100}%`,
                        top: `${scrollFrac * (100 - thumbFrac * 100)}%`,
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => scrollList(1)}
                    aria-label="Scroll firms down"
                    className="w-5 h-5 flex items-center justify-center text-[rgba(20,33,61,.35)] hover:text-tan transition-colors"
                  >
                    <ChevronDown size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        ) : firmMatchStatus === 2 ? (
          <div className="mb-4 rounded-[10px] border border-[rgba(20,33,61,.1)] bg-ivory px-4 py-3">
            <p className="text-[13px] text-[rgba(20,33,61,.6)] font-plexsans">
              No matching law firms were found for this case yet.
            </p>
          </div>
        ) : (
          <AiRetryNotice
            type="firmMatch"
            status={firmMatchStatus}
            caseId={caseId}
            setUpdateComp={setUpdateComp}
            label="recommended firms"
          />
        )}
      </div>

    </div>
  );
}

export default AiFirms;
